// Dati dei pianeti: elementi orbitali approssimati JPL (E.M. Standish, «Keplerian Elements for Approximate
// Positions of the Major Planets», tabella valida 1800-2050). Nomi e schede stanno nei dizionari (t().corpi).
// Ogni elemento: [valore a J2000, variazione per secolo]. a in UA, angoli in gradi.

export interface ElementiPianeta {
  a: [number, number];
  e: [number, number];
  i: [number, number];
  L: [number, number];
  peri: [number, number];
  nodo: [number, number];
}

export interface Pianeta {
  id: string;
  nome: string;
  raggioScena: number;
  texture: string;
  elementi: ElementiPianeta | null;
  inclinazioneAsse: number;
}

export const PIANETI: Pianeta[] = [
  {
    id: 'mercurio', nome: 'Mercurio', raggioScena: 0.32, texture: 'mercurio', inclinazioneAsse: 0.03,
    elementi: { a: [0.38709927, 0.00000037], e: [0.20563593, 0.00001906], i: [7.00497902, -0.00594749], L: [252.2503235, 149472.67411175], peri: [77.45779628, 0.16047689], nodo: [48.33076593, -0.12534081] },
  },
  {
    id: 'venere', nome: 'Venere', raggioScena: 0.55, texture: 'venere', inclinazioneAsse: 177.4,
    elementi: { a: [0.72333566, 0.0000039], e: [0.00677672, -0.00004107], i: [3.39467605, -0.0007889], L: [181.9790995, 58517.81538729], peri: [131.60246718, 0.00268329], nodo: [76.67984255, -0.27769418] },
  },
  {
    id: 'terra', nome: 'Terra', raggioScena: 0.58, texture: 'terra', inclinazioneAsse: 23.44,
    elementi: { a: [1.00000261, 0.00000562], e: [0.01671123, -0.00004392], i: [-0.00001531, -0.01294668], L: [100.46457166, 35999.37244981], peri: [102.93768193, 0.32327364], nodo: [0, 0] },
  },
  {
    id: 'marte', nome: 'Marte', raggioScena: 0.42, texture: 'marte', inclinazioneAsse: 25.19,
    elementi: { a: [1.52371034, 0.00001847], e: [0.0933941, 0.00007882], i: [1.84969142, -0.00813131], L: [-4.55343205, 19140.30268499], peri: [-23.94362959, 0.44441088], nodo: [49.55953891, -0.29257343] },
  },
  {
    id: 'giove', nome: 'Giove', raggioScena: 1.7, texture: 'giove', inclinazioneAsse: 3.13,
    elementi: { a: [5.202887, -0.00011607], e: [0.04838624, -0.00013253], i: [1.30439695, -0.00183714], L: [34.39644051, 3034.74612775], peri: [14.72847983, 0.21252668], nodo: [100.47390909, 0.20469106] },
  },
  {
    id: 'saturno', nome: 'Saturno', raggioScena: 1.45, texture: 'saturno', inclinazioneAsse: 26.73,
    elementi: { a: [9.53667594, -0.0012506], e: [0.05386179, -0.00050991], i: [2.48599187, 0.00193609], L: [49.95424423, 1222.49362201], peri: [92.59887831, -0.41897216], nodo: [113.66242448, -0.28867794] },
  },
  {
    id: 'urano', nome: 'Urano', raggioScena: 1.0, texture: 'urano', inclinazioneAsse: 97.77,
    elementi: { a: [19.18916464, -0.00196176], e: [0.04725744, -0.00004397], i: [0.77263783, -0.00242939], L: [313.23810451, 428.48202785], peri: [170.9542763, 0.40805281], nodo: [74.01692503, 0.04240589] },
  },
  {
    id: 'nettuno', nome: 'Nettuno', raggioScena: 0.98, texture: 'nettuno', inclinazioneAsse: 28.32,
    elementi: { a: [30.06992276, 0.00026291], e: [0.00859048, 0.00005105], i: [1.77004347, 0.00035372], L: [-55.12002969, 218.45945325], peri: [44.96476227, -0.32241464], nodo: [131.78422574, -0.00508664] },
  },
];

const rad = (g: number) => (g * Math.PI) / 180;

/** Posizione eliocentrica (UA, eclittica J2000: x, y nel piano, z verso nord) alla data giuliana jd. */
export function posizioneEliocentrica(el: ElementiPianeta, jd: number): [number, number, number] {
  const T = (jd - 2451545.0) / 36525;
  const v = (k: [number, number]) => k[0] + k[1] * T;
  const a = v(el.a), e = v(el.e), I = rad(v(el.i)), L = v(el.L), peri = v(el.peri), nodo = v(el.nodo);
  const omega = rad(peri - nodo);
  const O = rad(nodo);
  let M = ((L - peri) % 360 + 540) % 360 - 180;
  M = rad(M);
  let E = M + e * Math.sin(M);
  for (let k = 0; k < 8; k++) E -= (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
  const xp = a * (Math.cos(E) - e);
  const yp = a * Math.sqrt(1 - e * e) * Math.sin(E);
  const co = Math.cos(omega), so = Math.sin(omega), cO = Math.cos(O), sO = Math.sin(O), cI = Math.cos(I), sI = Math.sin(I);
  return [
    (co * cO - so * sO * cI) * xp + (-so * cO - co * sO * cI) * yp,
    (co * sO + so * cO * cI) * xp + (-so * sO + co * cO * cI) * yp,
    so * sI * xp + co * sI * yp,
  ];
}

/** Periodo orbitale in giorni (terza legge di Keplero). */
export const periodoGiorni = (a: number) => 365.25 * Math.pow(a, 1.5);

/** Compressione delle distanze per la scena: radice quadrata, così Mercurio e Nettuno stanno nello stesso schermo. */
export const SCALA_DISTANZA = 14;
export function comprimi(x: number, y: number, z: number): [number, number, number] {
  const r = Math.hypot(x, y, z);
  if (r === 0) return [0, 0, 0];
  const k = (SCALA_DISTANZA * Math.sqrt(r)) / r;
  // eclittica → scena: x = x, y (scena, in alto) = z (nord eclittica), z = −y
  return [x * k, z * k, -y * k];
}

/** Raggio del Sole nella scena del sistema solare (ingrandito: in scala sarebbe un punto). */
export const RAGGIO_SOLE = 2.6;

/** Posizione eliocentrica (UA) di una missione alla data giuliana jd, interpolata tra i punti; null fuori dai dati. */
export function posizioneMissione(punti: [number, number, number, number][], jd: number): [number, number, number] | null {
  if (!punti.length || jd < punti[0][0] || jd > punti[punti.length - 1][0]) return null;
  let i = 1;
  while (i < punti.length - 1 && punti[i][0] < jd) i++;
  const [g0, x0, y0, z0] = punti[i - 1];
  const [g1, x1, y1, z1] = punti[i];
  const k = g1 === g0 ? 0 : (jd - g0) / (g1 - g0);
  return [x0 + (x1 - x0) * k, y0 + (y1 - y0) * k, z0 + (z1 - z0) * k];
}
